import React from 'react';
import { cn } from '@/lib/utils';
import type { ContentBlock } from './types/messageContent';

interface StructuredMessageRendererProps {
  blocks: ContentBlock[];
  currentUserEmail?: string;
  className?: string;
  onMentionClick?: (userEmail: string) => void;
}

/**
 * StructuredMessageRenderer - Renders structured message content (no regex parsing!)
 *
 * Instead of parsing "@Ross McGowan test" with regex, this receives structured blocks:
 * [
 *   { type: 'mention', email: '...', displayName: 'Ross McGowan' },
 *   { type: 'text', content: ' test' }
 * ]
 *
 * Benefits:
 * - No regex parsing = no ambiguity
 * - Mentions are always styled as chips
 * - Easy to customize mention appearance
 * - Clear separation between text and mentions
 */
export const StructuredMessageRenderer: React.FC<StructuredMessageRendererProps> = ({
  blocks,
  currentUserEmail,
  className,
  onMentionClick
}) => {
  const handleMentionClick = (userEmail: string) => {
    if (onMentionClick) {
      onMentionClick(userEmail);
    }
  };

  return (
    <span className={cn('inline-block break-words whitespace-normal', className)}>
      {blocks.map((block, index) => {
        if (block.type === 'mention') {
          const isCurrentUser = block.email === currentUserEmail;

          return (
            <span
              key={index}
              className={cn(
                'inline-block px-1 py-0.5 rounded text-sm font-medium cursor-pointer transition-colors',
                isCurrentUser
                  ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                  : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
              )}
              onClick={() => handleMentionClick(block.email)}
              title={`Mentioned: ${block.displayName} (${block.email})`}
            >
              @{block.displayName}
            </span>
          );
        }

        // Text block
        return (
          <span key={index} className="inline-block break-words whitespace-normal">
            {block.content}
          </span>
        );
      })}
    </span>
  );
};

